import React, { useCallback } from 'react';
import {
  Box,
  Flex,
  Text,
  Progress,
  HStack,
  Avatar,
  AvatarGroup,
  Badge,
  Menu,
  useDisclosure,
} from '@chakra-ui/react';
import { IconButton } from 'src/components/atoms/IconButton';
import { MenuButton } from 'src/components/atoms/MenuButton';
import { MenuList } from 'src/components/molecules/MenuList';
import { MenuOption } from 'src/components/atoms/MenuOption';
import { Icon } from 'src/components/atoms/Icon';
import { Goal } from 'src/features/Goals/types';
import { useCurrency } from 'src/hooks/useCurrency';
import {
  formatMetricValue,
  formatTargetDisplay,
  formatGoalDate,
  getRemainingLabel,
  STATUS_DISPLAY,
  STATUS_COLORS,
} from 'src/features/Goals/utils/formatters';

type GoalCardProps = {
  goal: Goal;
  onViewDetails: (goal: Goal) => void;
  onViewLinkedData: (goal: Goal) => void;
  onEdit: (goal: Goal) => void;
  onDelete: (goal: Goal) => void;
};

export const GoalCard: React.FC<GoalCardProps> = ({
  goal,
  onViewDetails,
  onViewLinkedData,
  onEdit,
  onDelete,
}) => {
  const statusConfig = STATUS_COLORS[goal.status];
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { code: currencyCode } = useCurrency();

  const handleViewDetails = useCallback(() => {
    onClose();
    onViewDetails(goal);
  }, [onClose, onViewDetails, goal]);

  const handleViewLinkedData = useCallback(() => {
    onClose();
    onViewLinkedData(goal);
  }, [onClose, onViewLinkedData, goal]);

  const handleEdit = useCallback(() => {
    onClose();
    onEdit(goal);
  }, [onClose, onEdit, goal]);

  const handleDelete = useCallback(() => {
    onClose();
    onDelete(goal);
  }, [onClose, onDelete, goal]);

  return (
    <Box
      bg="surface.panel"
      borderRadius="12px"
      border="1px solid"
      borderColor="surface.border"
      p={5}
      _hover={{ boxShadow: 'sm' }}
      transition="box-shadow 0.2s">
      {/* Header: Status + Menu */}
      <Flex justify="space-between" align="flex-start" mb={3}>
        <Badge
          bg={statusConfig.bg}
          color={statusConfig.color}
          px={3}
          py={1}
          borderRadius="full"
          fontWeight="medium"
          fontSize="xs">
          {STATUS_DISPLAY[goal.status]}
        </Badge>

        <Menu isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
          <MenuButton
            buttonName="goal-card-menu"
            as={IconButton}
            aria-label="Goal actions"
            bg="transparent"
            p={0}
            minW="auto"
            h="auto"
            _hover={{ bg: 'transparent' }}
            _active={{ bg: 'transparent' }}>
            <Box as="span" fontSize="20px" lineHeight="1" color="gray.600">
              ⋯
            </Box>
          </MenuButton>
          <MenuList buttonName="goal-card-menu">
            <MenuOption
              buttonName="goal-view-details"
              label="View Details"
              value="view-details"
              isSelected={false}
              onClick={handleViewDetails}
            />
            <MenuOption
              buttonName="goal-view-linked"
              label="View Linked Data"
              value="view-linked"
              isSelected={false}
              onClick={handleViewLinkedData}
            />
            <MenuOption
              buttonName="goal-edit"
              label="Edit"
              value="edit"
              isSelected={false}
              onClick={handleEdit}
            />
            <MenuOption
              buttonName="goal-delete"
              label="Delete"
              value="delete"
              isSelected={false}
              onClick={handleDelete}
              color="red.500"
            />
          </MenuList>
        </Menu>
      </Flex>

      {/* Title */}
      <Text
        fontWeight="bold"
        fontSize="md"
        color="content.title"
        mb={2}
        noOfLines={1}>
        {goal.title}
      </Text>

      {/* Target Value */}
      <Text fontSize="lg" fontWeight="semibold" color="content.title" mb={3}>
        {formatTargetDisplay(
          goal.targetValue,
          goal.metric,
          goal.kpiUnit,
          currencyCode,
        )}
      </Text>

      {/* Progress Bar */}
      <Progress
        value={goal.progressPercent}
        size="sm"
        colorScheme="green"
        borderRadius="full"
        mb={2}
      />

      {/* Remaining Value + Percentage */}
      <Flex justify="space-between" align="center" mb={3}>
        <Text fontSize="sm" color="gray.600">
          {formatMetricValue(
            goal.remainingValue,
            goal.metric,
            goal.kpiUnit,
            currencyCode,
          )}{' '}
          <Text as="span" color="gray.400">
            {getRemainingLabel(goal.metric)}
          </Text>
        </Text>
        <Text fontSize="sm" color="gray.500">
          {Math.round(goal.progressPercent)}%
        </Text>
      </Flex>

      {/* Footer: Date + Comments + Avatars */}
      <Flex justify="space-between" align="center">
        <Text fontSize="sm" color="gray.500">
          {formatGoalDate(goal.goalPeriodEnd)}
        </Text>

        <HStack spacing={3}>
          {goal.commentsCount > 0 && (
            <HStack spacing={1}>
              <Icon iconName="comment" boxSize={4} color="gray.400" />
              <Text fontSize="sm" color="gray.500">
                {goal.commentsCount}
              </Text>
            </HStack>
          )}

          {goal.mentionedUsers.length > 0 && (
            <AvatarGroup size="xs" max={3}>
              {goal.mentionedUsers.map((user) => (
                <Avatar
                  key={user.id}
                  name={user.name}
                  src={user.avatarUrl}
                  size="xs"
                />
              ))}
            </AvatarGroup>
          )}
        </HStack>
      </Flex>
    </Box>
  );
};
